import React, { useMemo } from 'react';
import { Field, ErrorMessage, useFormikContext } from 'formik';
import { Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
    CreditCard, Banknote, Landmark, ShieldCheck, X,
    ArrowRightLeft, BadgeCheck, AlertCircle
} from 'lucide-react';
import { formatCurrency } from '../../utility';
import ShiftAwareAccountPicker from '../../shared/components/ShiftAwareAccountPicker';


const PaymentMethodRefined = ({ index, remove }) => {
    const { values, setFieldValue } = useFormikContext();
    const { accounts } = useSelector((state) => state.accountsReducer);
    const method = values.payment_methods[index].method;
    const currentAmount = values.payment_methods[index].amount;

    // Filter company accounts for potential routing (for Bank/Card methods)
    const companyAccounts = accounts.filter(acc => acc.account_owner_type === 'Company');
    const bankAccounts = companyAccounts.filter(acc =>
        acc.account_name.toLowerCase().includes('bank') || acc.account_type === 'Bank' || acc.account_type === 'Mobile Money'
    );

    const getIcon = () => {
        switch (method) {
            case 'Card': return <CreditCard className="text-primary" size={18} />;
            case 'Cash': return <Banknote className="text-success" size={18} />;
            case 'Bank Transfer': return <Landmark className="text-info" size={18} />;
            case 'Cheque': return <ArrowRightLeft className="text-warning" size={18} />;
            default: return <Banknote className="text-muted" size={18} />;
        }
    };

    // FINANCIAL VALIDATION: Check if method is compatible with Source Account
    const isCompatible = useMemo(() => {
        const fromAccId = values.payment_methods[index].from_account_id;
        const fromAcc = accounts.find(a => a._id === fromAccId);
        if (!fromAcc) return true;

        const isSourceBank = fromAcc.account_name.toLowerCase().includes('bank') || fromAcc.account_type === 'Bank' || fromAcc.account_type === 'Mobile Money';
        const isSourceCash = fromAcc.account_name.toLowerCase().includes('cash');

        if (isSourceCash && (method === 'Bank Transfer' || method === 'Cheque' || method === 'Online')) return false;
        if (isSourceBank && method === 'Cash') return false;

        return true;
    }, [values.payment_methods[index].from_account_id, method, accounts]);

    return (
        <div className={`payment-method-glass ${!isCompatible ? 'validation-error' : ''}`}>
            <style>
                {`
            .payment-method-glass {
                background: white;
                border: 2px solid #f1f5f9;
                border-radius: 20px;
                padding: 24px;
                position: relative;
                transition: all 0.3s;
                overflow: hidden;
            }
            .payment-method-glass:hover {
                border-color: #cbd5e1;
                transform: translateX(4px);
                box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05);
            }
            .payment-method-glass.validation-error {
                border-color: #fee2e2;
                background: #fffafa;
            }
            .remove-btn-refined {
                position: absolute;
                top: 15px;
                right: 15px;
                background: #f1f5f9;
                border: none;
                width: 28px;
                height: 28px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #64748b;
                transition: all 0.2s;
                z-index: 10;
            }
            .remove-btn-refined:hover {
                background: #fee2e2;
                color: #ef4444;
            }
            .premium-sub-label {
                font-size: 10px;
                font-weight: 800;
                color: #94a3b8;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin-bottom: 8px;
                display: block;
            }
            .method-selector-refined {
                border: 2px solid #f1f5f9;
                border-radius: 12px;
                padding: 8px 12px;
                font-weight: 700;
                font-size: 13px;
                width: 100%;
                outline: none;
                color: #0f172a;
                background: #f8fafc;
            }
            .amount-input-refined {
                border: 2px solid #f1f5f9;
                border-radius: 12px;
                padding: 8px 15px;
                font-weight: 900;
                font-size: 15px;
                width: 100%;
                background: #f8fafc;
                text-align: right;
                outline: none;
            }
            .incompatibility-warning {
                background: #ef4444;
                color: white;
                padding: 4px 12px;
                font-size: 9px;
                font-weight: 900;
                text-transform: uppercase;
                border-radius: 0 0 10px 10px;
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                text-align: center;
            }
        `}
            </style>

            <button type="button" className="remove-btn-refined" onClick={() => remove(index)}>
                <X size={14} />
            </button>

            <Row className="g-3 align-items-center">
                <Col md={3}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                        {getIcon()}
                        <span className="premium-sub-label mb-0">Method</span>
                    </div>
                    <Field
                        as="select"
                        name={`payment_methods[${index}].method`}
                        className="method-selector-refined"
                    >
                        <option value="Cash">Cash Liquidity</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Card">Terminal / Card</option>
                        <option value="Cheque">Corporate Cheque</option>
                    </Field>
                </Col>

                <Col md={3}>
                    <span className="premium-sub-label">Allocated Amount</span>
                    <div className="position-relative">
                        <span className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', fontWeight: 800, color: '#94a3b8', fontSize: '11px' }}>LKR</span>
                        <Field
                            name={`payment_methods[${index}].amount`}
                            type="number"
                            className="amount-input-refined"
                            placeholder="0.00"
                            onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                setFieldValue(`payment_methods[${index}].amount`, val);

                                // Sync with global amount if only 1 method
                                if (values.payment_methods.length === 1) {
                                    setFieldValue("amount", val);
                                }
                            }}
                        />
                    </div>
                </Col>

                <Col md={6}>
                    <ShiftAwareAccountPicker
                        accounts={companyAccounts}
                        value={values.payment_methods[index].from_account_id || ''}
                        onChange={(id) => setFieldValue(`payment_methods[${index}].from_account_id`, id)}
                        amount={currentAmount}
                        transactionDirection="out"
                        label="Pay From Account"
                        allowUnattendedDrawer={true}
                    />
                </Col>

                <Col md={3}>
                    {(method === 'Bank Transfer' || method === 'Card') && (
                        <div className="animate__animated animate__fadeIn">
                            <span className="premium-sub-label">Target Ledger</span>
                            <Field
                                as="select"
                                name={`payment_methods[${index}].target_account_id`}
                                className="method-selector-refined"
                            >
                                <option value="">Default Routing</option>
                                {bankAccounts.map(acc => (
                                    <option key={acc._id} value={acc._id}>{acc.account_name}</option>
                                ))}
                            </Field>
                        </div>
                    )}
                    {method === 'Cash' && (
                        <div className="text-center opacity-50">
                            <span className="premium-sub-label">Instant Settlement</span>
                            <BadgeCheck size={18} className="text-success" />
                        </div>
                    )}
                </Col>
            </Row>

            {!isCompatible && (
                <div className="incompatibility-warning animate__animated animate__slideInUp">
                    <AlertCircle size={10} className="me-1" /> Financial Discord: Incompatible source account
                </div>
            )}
        </div>
    );
};

export default PaymentMethodRefined;
